import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Sparkles, ArrowRight, CheckCircle, Monitor } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_fa5ebc01-0c7f-4b55-8e38-1fa2a64145ae/artifacts/u6f2dm1t_CompliSun.png';
const FOX_MASCOT = 'https://customer-assets.emergentagent.com/job_fa5ebc01-0c7f-4b55-8e38-1fa2a64145ae/artifacts/28ykjv9m_Transparent%20BG%20Fox.png';

const Login = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [showNameInput, setShowNameInput] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(true);
  const { requestMagicLink } = useAuth();

  // Clear stale tokens when landing on login
  useEffect(() => {
    localStorage.removeItem('access_token');
  }, []);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.endsWith('@complisun.com')) {
      toast.error('Only @complisun.com email addresses are allowed');
      return;
    }

    setLoading(true);

    try {
      const result = await requestMagicLink(email, name);
      
      // Store remember preference so VerifyMagicLink can use it
      localStorage.setItem('remember_device_pref', rememberDevice ? 'true' : 'false');
      
      setMagicLinkSent(true);
      toast.success('Magic link sent! Check your email.');
      
      if (result.is_new_user && !name) {
        setShowNameInput(true);
        setMagicLinkSent(false);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send magic link. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setMagicLinkSent(false);
    setLoading(true);
    try {
      await requestMagicLink(email, name);
      localStorage.setItem('remember_device_pref', rememberDevice ? 'true' : 'false');
      setMagicLinkSent(true);
      toast.success('New magic link sent!');
    } catch (error) {
      toast.error('Failed to resend magic link');
    } finally {
      setLoading(false);
    }
  };

  if (magicLinkSent) {
    return (
      <div className="min-h-screen bg-[#1A2B5E] flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="bg-white rounded-xl p-8 shadow-xl">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#37C563]/10 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-[#37C563]" />
            </div>
            
            <h2 className="text-2xl font-bold text-[#1A2B5E] font-['Amaranth']" data-testid="magic-link-sent-title">Check Your Email!</h2>
            
            <p className="text-navy/60 mt-4">
              We sent a magic link to <span className="font-medium text-[#1A2B5E]">{email}</span>
            </p>
            
            <p className="text-sm text-navy/50 mt-2">
              Click the link in the email to sign in. The link expires in 15 minutes.
            </p>

            {rememberDevice && (
              <div className="mt-4 flex items-center justify-center gap-2 text-sm text-[#40CFDE]">
                <Monitor className="w-4 h-4" />
                <span>This device will be remembered for 30 days</span>
              </div>
            )}
            
            <div className="mt-8 p-4 bg-[#F5F8FF] rounded-lg">
              <img src={FOX_MASCOT} alt="Fox" className="w-16 h-16 mx-auto mb-2" />
              <p className="text-sm text-navy/60 italic">
                "No passwords to remember. Just pure compliance magic!"
              </p>
            </div>
            
            <div className="mt-6 space-y-3">
              <Button
                variant="outline"
                className="w-full"
                onClick={handleResend}
                disabled={loading}
                data-testid="resend-magic-link-btn"
              >
                {loading ? 'Sending...' : 'Resend Magic Link'}
              </Button>
              
              <button
                className="text-sm text-[#106EFD] hover:underline"
                onClick={() => {
                  setMagicLinkSent(false);
                  setEmail('');
                }}
                data-testid="use-different-email-btn"
              >
                Use a different email
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1A2B5E] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and header */}
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="CompliSun" className="w-20 h-20 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white font-['Amaranth']">CompliSun Ops Hub</h1>
          <p className="text-white/70 mt-2">Welcome, CompliCrew!</p>
        </div>

        {/* Magic Link form */}
        <div className="bg-white rounded-xl p-8 shadow-xl" data-testid="login-form">
          <div className="flex items-center gap-3 mb-6 p-3 bg-[#40CFDE]/10 rounded-lg">
            <Sparkles className="w-5 h-5 text-[#40CFDE]" />
            <p className="text-sm text-[#1A2B5E]">
              <span className="font-medium">Passwordless login</span> - We'll email you a magic link
            </p>
          </div>

          <form onSubmit={handleEmailSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email">Work Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-navy/40" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@complisun.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="h-12 pl-11"
                />
              </div>
              <p className="text-xs text-navy/50">Only @complisun.com addresses</p>
            </div>

            {showNameInput && (
              <div className="space-y-2">
                <Label htmlFor="name">Your Name</Label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Your full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="login-name-input"
                  className="h-12"
                />
                <p className="text-xs text-navy/50">For your new account</p>
              </div>
            )}

            {/* Remember this device toggle */}
            <label
              className="flex items-center gap-3 cursor-pointer group"
              data-testid="remember-device-toggle"
            >
              <div className="relative">
                <input
                  type="checkbox"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-10 h-5 bg-gray-200 rounded-full peer-checked:bg-[#40CFDE] transition-colors" />
                <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow peer-checked:translate-x-5 transition-transform" />
              </div>
              <div className="flex items-center gap-2">
                <Monitor className="w-4 h-4 text-navy/50 group-hover:text-[#40CFDE] transition-colors" />
                <span className="text-sm text-navy/70 group-hover:text-navy transition-colors">
                  Remember this device for 30 days
                </span>
              </div>
            </label>

            <Button
              type="submit"
              className="w-full h-12 bg-[#40CFDE] text-[#1A2B5E] hover:bg-[#40CFDE]/90 font-medium text-base"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                'Sending Magic Link...'
              ) : (
                <>
                  Send Magic Link
                  <ArrowRight className="w-5 h-5 ml-2" />
                </>
              )}
            </Button>
          </form>

          {!showNameInput && (
            <button
              className="w-full mt-4 text-sm text-[#106EFD] hover:underline"
              onClick={() => setShowNameInput(true)}
            >
              First time? Enter your name too
            </button>
          )}
        </div>

        {/* ISM Quote */}
        <p className="text-center text-white/50 text-sm mt-8 italic">
          "Every Client. Every Time. No Exceptions. No Excuses."
        </p>
      </div>
    </div>
  );
};

export default Login;
